import React, { createContext, useContext, useState, ReactNode } from 'react'

interface LegacyIframesContextType {
  // Legacy CRM
  isLegacyCRMIframeVisible: boolean
  enableCRMIframeVisibility: () => void
  disableCRMIframeVisibility: () => void

  // Dashboard Iframe
  isDashboardIframeVisible: boolean
  enableDashboardIframeVisibility: () => void
  disableDashboardIframeVisibility: () => void

  // Trust Iframe
  isTrustIframeVisible: boolean
  enableTrustIframeVisibility: () => void
  disableTrustIframeVisibility: () => void

  // Legacy Iframe Loaded state
  isLegacyCRMIframeLoaded: boolean
  setIsLegacyCRMIframeLoaded: (isLoaded: boolean) => void

  // Current legacy pagename state
  legacyCRMIframePageName: string
  setLegacyCRMIframePageName: (pageName: string) => void
}

const LegacyIframesVisibilityContext = createContext<LegacyIframesContextType | undefined>(undefined)

/**
 * LegacyIframesVisibilityContext provides visibility, loading, and navigation
 * state management for embedding the legacy CRM/Dashboard/Trust iframe within the application.
 *
 * ## Visibility Flags:
 * - `isLegacyCRMIframeVisible`: Whether the legacy CRM iframe is shown.
 * - `isDashboardIframeVisible`: Whether the dashboard iframe is shown.
 * - `isTrustIframeVisible`: Whether the trust iframe is shown.
 *
 * ## Visibility Actions:
 * - `enableCRMIframeVisibility()`: Shows the CRM iframe and hides others.
 * - `disableCRMIframeVisibility()`: Hides the CRM iframe.
 * - `enableDashboardIframeVisibility()`: Shows dashboard iframe, hides others.
 * - `disableDashboardIframeVisibility()`: Hides the dashboard iframe.
 * - `enableTrustIframeVisibility()`: Shows the trust iframe, hides others.
 * - `disableTrustIframeVisibility()`: Hides the trust iframe.
 *
 * ## Lifecycle Tracking:
 * - `isLegacyCRMIframeLoaded`: Whether the legacy CRM iframe has finished loading.
 * - `setIsLegacyCRMIframeLoaded()`: Updates the loading state.
 *
 * ## Navigation Tracking:
 * - `legacyCRMIframePageName`: The current pathname (e.g., '/trust') from React Router.
 * - `setLegacyCRMIframePageName()`: Updates the tracked pathname.
 *
 * ## Usage
 * Use `useLegacyIframesVisibilityContext()` inside components to access or modify
 * these values. Must be used within the `LegacyIframesVisibilityProvider`.
 */
export const useLegacyIframesVisibilityContext = (): LegacyIframesContextType => {
  const context = useContext(LegacyIframesVisibilityContext)
  if (!context) {
    throw new Error('useLegacyIframesVisibilityContext must be used within an LegacyIframesVisibilityProvider')
  }
  return context
}

interface LegacyIframesVisibilityProviderProps {
  children: ReactNode
}

export const LegacyIframesVisibilityProvider = ({ children }: LegacyIframesVisibilityProviderProps) => {
  const [isLegacyCRMIframeVisible, setIsLegacyCRMIframeVisible] = useState(false)
  const [isDashboardIframeVisible, setIsDashboardIframeVisible] = useState(false)
  const [isTrustIframeVisible, setIsTrustIframeVisible] = useState(false)

  const [isLegacyCRMIframeLoaded, setIsLegacyCRMIframeLoaded] = useState(false)
  const [legacyCRMIframePageName, setLegacyCRMIframePageName] = useState('')

  const enableCRMIframeVisibility = () => {
    setIsLegacyCRMIframeVisible(true)
    setIsDashboardIframeVisible(false)
    setIsTrustIframeVisible(false)
  }
  const disableCRMIframeVisibility = () => setIsLegacyCRMIframeVisible(false)

  const enableDashboardIframeVisibility = () => {
    setIsDashboardIframeVisible(true)
    setIsLegacyCRMIframeVisible(false)
    setIsTrustIframeVisible(false)
  }
  const disableDashboardIframeVisibility = () => setIsDashboardIframeVisible(false)

  const enableTrustIframeVisibility = () => {
    setIsTrustIframeVisible(true)
    setIsLegacyCRMIframeVisible(false)
    setIsDashboardIframeVisible(false)
  }
  const disableTrustIframeVisibility = () => setIsTrustIframeVisible(false)

  return (
    <LegacyIframesVisibilityContext.Provider
      value={{
        isLegacyCRMIframeVisible,
        enableCRMIframeVisibility,
        disableCRMIframeVisibility,

        isDashboardIframeVisible,
        enableDashboardIframeVisibility,
        disableDashboardIframeVisibility,

        isTrustIframeVisible,
        enableTrustIframeVisibility,
        disableTrustIframeVisibility,

        isLegacyCRMIframeLoaded,
        setIsLegacyCRMIframeLoaded,

        legacyCRMIframePageName,
        setLegacyCRMIframePageName,
      }}
    >
      {children}
    </LegacyIframesVisibilityContext.Provider>
  )
}
